import React from 'react';

import FaqDrawer from 'dpl/common/components/FaqDrawer';
import {
  HEALTH_TESTING_GUIDE_PATH,
  PAYMENT_PROTECTION_POLICY_PATH,
  STANDARDS_PATH
} from 'dpl/shared/constants/urls';
import { isMobileUA } from 'dpl/shared/utils';

import {
  BREEDER_VETTING_CRITERIA,
  CERTIFIED_BREEDER_PROCESS,
  HEALTH_TEST_REQUIREMENT_SOURCES
} from '../utils/constants';

const FAQ_ITEMS = [
  {
    question: 'What are our Community Standards?',
    answer: (
      <p>
        Our community standards are crafted with the guidance of leading experts
        in canine reproductive health and outlined in our Good Breeder Code of
        Ethics. These standards are grounded in evidence-based research and
        backed by science, reflecting our commitment to ethical breeding
        practices and making it easy for buyers to connect with reputable
        breeders.
        <br />
        <br />
        While there is no one-size-fits-all approach to assessing breeding
        practices, our rigorous certification process verifies that every Good
        Dog breeder meets or exceeds our community standards before earning
        certification. These standards ensure breeders prioritize the mental and
        behavioral health of their dogs while providing the necessary support
        for new puppy owners.{' '}
        <a
          href={STANDARDS_PATH}
          className="underline"
          target="_blank"
          rel="noopener noreferrer"
        >
          Learn more about our community standards
        </a>
        .
      </p>
    )
  },
  {
    question: 'How are Good Dog Breeders vetted?',
    answer: (
      <div>
        <p className="mb4">
          Every breeder in our community is thoroughly vetted by our in-house
          screening team to ensure they meet or exceed the community standards
          outlined in our Good Breeder Code of Ethics. We ensure each breeder
          meets or exceeds our criteria for:
        </p>
        <ul className="list--disc pl4 mb4">
          {BREEDER_VETTING_CRITERIA.map((item, idx) => (
            <li key={idx}>{item}</li>
          ))}
        </ul>
        <p>
          Each of these elements is rigorously screened before a breeder
          receives a certification from Good Dog.{' '}
          <a
            href={STANDARDS_PATH}
            className="underline"
            target="_blank"
            rel="noopener noreferrer"
          >
            Learn more about our screening process
          </a>
          .
        </p>
      </div>
    )
  },
  {
    question: "Why should I trust Good Dog's screening process?",
    answer: (
      <div>
        <p className="mb4">
          We encourage buyers to research and find the right reputable breeder
          for them, but our thorough screening process adds an extra layer of
          security and peace of mind.
          <br />
          <br />
          Every Good Dog breeder must:
        </p>
        <ul className="list--disc pl4 mb4">
          {CERTIFIED_BREEDER_PROCESS.map((item, idx) => (
            <li key={idx}>{item}</li>
          ))}
        </ul>
        <p>
          Additionally, all transactions are protected by the{' '}
          <a
            href={PAYMENT_PROTECTION_POLICY_PATH}
            className="underline"
            target="_blank"
            rel="noopener noreferrer"
          >
            Good Dog Payment Protection
          </a>{' '}
          providing security and support throughout your puppy-buying journey.
        </p>
      </div>
    )
  },
  {
    question:
      'Are Good Dog Breeders required to health test their parent dogs?',
    answer: (
      <p>
        Health testing is a key part of our screening process. Our dedicated
        team reviews the health tests breeders perform on their dogs to ensure
        they meet or exceed breed-specific health testing requirements. To
        promote transparency, breeders can share health testing documentation on
        their Good Dog profiles, allowing buyers to access these records
        directly under the &quot;Moms and Dads&quot; section when viewing a
        specific dog.{' '}
        <a
          href={HEALTH_TESTING_GUIDE_PATH}
          className="underline"
          target="_blank"
          rel="noopener noreferrer"
        >
          Learn more about our approach to health testing
        </a>
        .
      </p>
    )
  },
  {
    question: 'How are health testing requirements determined?',
    answer: (
      <div>
        <p className="mb4">
          Our community standards for health testing were developed with the
          guidance of leading reproductive experts, veterinary professionals,
          and breed specialists. These standards are tailored to address the
          unique history and health concerns of each breed.
          <br />
          <br />
          Good Dog&apos;s health testing requirements are based on:
        </p>
        <ul className="list--disc pl4 mb4">
          {HEALTH_TEST_REQUIREMENT_SOURCES.map((item, idx) => (
            <li key={idx}>{item}</li>
          ))}
        </ul>
        <p>
          We regularly review and update our standards to reflect the latest in
          canine health research and breeding practices. Reputable breeders on
          Good Dog are recognized at three levels—Good, Great, or
          Excellent—based on the number of recommended health tests performed.{' '}
          <a
            href={HEALTH_TESTING_GUIDE_PATH}
            className="underline"
            target="_blank"
            rel="noopener noreferrer"
          >
            Learn more about our approach to health testing
          </a>
          .
        </p>
      </div>
    )
  },
  {
    question: 'Is it safe to buy a puppy online?',
    answer: (
      <p>
        Searching for a puppy online can be overwhelming, with risks of
        encountering scams, fraud, and bad actors. That&apos;s where Good Dog
        comes in—we&apos;re committed to providing a safe, trustworthy way to
        find your perfect puppy.
        <br />
        <br />
        Getting a puppy on Good Dog works differently—you communicate directly
        with your breeder through our secure messaging system, receive regular
        updates, and develop a partnership. Our rigorous screening and secure
        processes ensure peace of mind every step of the way. For your
        protection, all payments go through our secure system, backed by Good
        Dog Payment Protection. Good Dog Payment Protection ensures a safe
        process for both you and your breeder, and if anything goes wrong during
        or after the process, our Care Team is here to help resolve any issues.
      </p>
    )
  }
];

const IS_MOBILE_UA = isMobileUA();

export default function FaqsSection() {
  return (
    <div className="ReviewsFaqsSection container-xxl pv20-lg pv10">
      {IS_MOBILE_UA ? (
        <h4 className="font-24 mb2">Frequently Asked Questions</h4>
      ) : (
        <p className="font-16 fw-medium mb2">Frequently Asked Questions</p>
      )}
      <FaqDrawer
        faqs={FAQ_ITEMS}
        iconCloseName="plus-48"
        iconOpenName="minus-48"
        iconClassName="gray-600"
      />
    </div>
  );
}
